import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';

const ImageInput = ({
  onChange,
  error,
  showLabel = false,
  label = "Image",
  required = false,
  className = "",
  multiple = false,
  onProgress,
  existingImage = null, // URL of existing image for edit mode
  compressionOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  }
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Function to convert image to WebP
  const convertToWebP = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          // Convert to WebP
          canvas.toBlob((blob) => {
            if (blob) {
              // Create a new file with .webp extension
              const webpFile = new File(
                [blob],
                `image-${Date.now()}.webp`,
                {
                  type: 'image/webp',
                  lastModified: new Date().getTime()
                }
              );
              resolve(webpFile);
            } else {
              reject(new Error('WebP conversion failed'));
            }
          }, 'image/webp', 0.8);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Function to handle image compression and conversion
  const handleImageCompression = async (imageFile) => {
    try {
      // First compress the image
      const compressedFile = await imageCompression(imageFile, {
        ...compressionOptions,
        onProgress: (progress) => {
          // Scale compression progress to 0-80% (compression takes 80% of total time)
          const scaledProgress = Math.round(progress * 0.8);
          setProgress(scaledProgress);
          onProgress?.(scaledProgress);
        }
      });

      // Update progress to 90% for WebP conversion
      setProgress(90);
      onProgress?.(90);

      // Then convert to WebP
      const webpFile = await convertToWebP(compressedFile);

      // Complete progress
      setProgress(100);
      onProgress?.(100);

      console.log('Original file size:', imageFile.size / 1024 / 1024, 'MB');
      console.log('Compressed WebP file size:', webpFile.size / 1024 / 1024, 'MB');
      console.log('Converted to format:', webpFile.type);

      return webpFile;
    } catch (error) {
      console.error('Error processing image:', error);
      throw error;
    }
  };

  // Handle file selection
  const handleFileChange = async (event) => {
    if (!event?.target?.files?.length) {
      onChange(null);
      return;
    }

    const files = Array.from(event.target.files);
    setIsProcessing(true);
    setProgress(0);
    onProgress?.(0);

    try {
      if (multiple) {
        // Process multiple files
        const processedImages = await Promise.all(
          files.map(async (file, index) => {
            if (!file.type.startsWith('image/')) return null;

            // Calculate progress for each file
            const fileProgress = (index / files.length) * 100;
            setProgress(fileProgress);
            onProgress?.(fileProgress);

            return await handleImageCompression(file);
          })
        );
        const validImages = processedImages.filter(img => img !== null);
        onChange(validImages);
      } else {
        // Process single file
        const file = files[0];
        if (!file.type.startsWith('image/')) {
          onChange(null);
          return;
        }
        const processedImage = await handleImageCompression(file);
        onChange(processedImage);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      onChange(null);
    } finally {
      setIsProcessing(false);
      // Keep progress at 100% briefly before resetting
      setTimeout(() => {
        setProgress(0);
      }, 1000);
    }
  };

  // Handle custom button click
  const handleCustomButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="space-y-0">
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileChange}
          disabled={isProcessing}
          className="hidden"
        />

        {/* Custom File Input Design */}
        <div className="relative">
          <button
            type="button"
            onClick={handleCustomButtonClick}
            disabled={isProcessing}
            className={`w-full px-2 py-1 h-8 border ${error ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none transition-colors ${isProcessing ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-gray-400 bg-white'
              } flex items-center justify-between text-left`}
          >
            <span className="text-sm text-gray-500 truncate">
              {isProcessing ? 'Processing...' : 'Choose file'}
            </span>
            <div className="flex items-center space-x-2">
              {isProcessing && (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              )}
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
          </button>

          {/* Progress Bar Overlay */}
          {isProcessing && (
            <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex flex-col justify-center items-center p-4">
              <div className="w-full">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-primary">
                    Processing image...
                  </span>
                  <span className="text-xs font-medium text-primary">
                    {progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-red-500 text-xs mt-1">
            {error}
          </p>
        )}

        {/* Existing Image Info */}
        {/* {existingImage && (
                    <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-md">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Current image will be replaced when a new one is selected
                    </div>
                )} */}
      </div>
    </div>
  );
};

export default ImageInput; 